/**
 * ============================================================
 * HOBBLIVISION MATCHER
 * ============================================================
 *
 * Matcher V2
 *
 * PURPOSE
 * -------
 * Take structured clues produced by the parser and rank
 * possible collectible/card records.
 *
 * V2 introduces RELATIONAL EVIDENCE.
 *
 * V1 primarily asked:
 *
 *   "Does this field match?"
 *
 * V2 also asks:
 *
 *   "Do multiple pieces of evidence agree that this is the
 *    same item?"
 *
 * Example:
 *
 *   Parser:
 *     player      = Michael Jordan
 *     set         = Hoops
 *     year        = 1989
 *
 * Candidate A:
 *     Michael Jordan / 1989 Hoops
 *
 * Candidate B:
 *     Michael Jordan / 1991 Fleer
 *
 * Both may receive name evidence.
 * Candidate A receives additional relational evidence because
 * name + set + year form a coherent identity.
 *
 * The matcher DOES NOT:
 *
 *   - perform OCR
 *   - parse raw OCR
 *   - decide what OCR means
 *   - perform web searches
 *   - perform image similarity
 *
 * The matcher DOES:
 *
 *   - compare parser evidence against candidates
 *   - score direct evidence
 *   - evaluate relationships between evidence
 *   - penalize meaningful contradictions
 *   - preserve detailed diagnostics
 *   - return multiple candidates
 *
 * Future:
 *
 *   - image similarity
 *   - internet lookup
 *   - learned matching
 *
 * ============================================================
 */

const MATCHER_VERSION = "2.0";

/**
 * ============================================================
 * CONFIGURATION
 * ============================================================
 */

const CONFIG = {
  MAX_CANDIDATES: 10,

  MIN_CANDIDATE_SCORE: 0.20,

  /**
   * ----------------------------------------------------------
   * DIRECT EVIDENCE WEIGHTS
   * ----------------------------------------------------------
   *
   * These are the base signals.
   *
   * Relational evidence is added afterward.
   */
  WEIGHTS: {
    name: 0.38,
    set: 0.20,
    year: 0.15,
    cardNumber: 0.15,
    variation: 0.07,
    keyword: 0.02,
    additionalDetails: 0.03,
  },

  /**
   * ----------------------------------------------------------
   * RELATIONAL EVIDENCE
   * ----------------------------------------------------------
   *
   * These bonuses reward evidence that agrees with other
   * evidence.
   *
   * They are intentionally smaller than the primary signals.
   */
  RELATIONSHIPS: {
    nameSet: 0.06,
    nameYear: 0.04,
    setYear: 0.05,
    setCardNumber: 0.07,
    nameSetYear: 0.08,
    nameSetYearCardNumber: 0.06,
    variationSet: 0.04,
  },

  /**
   * ----------------------------------------------------------
   * CONTRADICTIONS
   * ----------------------------------------------------------
   *
   * Missing information is NOT a contradiction.
   *
   * A contradiction requires meaningful parser evidence and
   * meaningful candidate evidence.
   */
  CONTRADICTIONS: {
    set: 0.10,
    year: 0.10,
    cardNumber: 0.12,
    variation: 0.08,
  },

  /**
   * Fuzzy matching.
   */
  NAME_FUZZY_THRESHOLD: 0.65,

  SET_FUZZY_THRESHOLD: 0.65,

  VARIATION_FUZZY_THRESHOLD: 0.70,

  KEYWORD_FUZZY_THRESHOLD: 0.80,

  /**
   * Strong identifier handling.
   */
  EXACT_IDENTIFIER_BONUS: 0.06,

  /**
   * Evidence must have at least this confidence to participate
   * strongly in relational bonuses.
   */
  RELATIONSHIP_CONFIDENCE_THRESHOLD: 0.45,

  /**
   * Strong direct evidence threshold.
   *
   * Parser confidence for exact set/year/card-number matches is
   * typically 0.75-0.85 in this pipeline, so a 0.90 gate blocks
   * valid identity reinforcement.
   */
  STRONG_MATCH_THRESHOLD: 0.75,
};


/**
 * ============================================================
 * MAIN ENTRY POINT
 * ============================================================
 */

export function matchItems(parsedOCR, items = [], options = {}) {
  if (!parsedOCR) {
    return createEmptyMatchResult(
      "No parser result supplied."
    );
  }

  if (!Array.isArray(items)) {
    console.warn(
      "HobbliVision Matcher: items must be an array."
    );

    return createEmptyMatchResult(
      "Invalid candidate item list."
    );
  }

  if (!items.length) {
    return createEmptyMatchResult(
      "No candidate items supplied to matcher."
    );
  }

  const config = mergeConfig(options);

  /**
   * Normalize parser evidence once.
   *
   * V1 repeatedly extracted and normalized parser data while
   * scoring each field for every candidate.
   *
   * V2 prepares this once.
   */
  const parser = prepareParserEvidence(
    parsedOCR,
    options?.resolvedNameEvidence
  );

  /**
   * ============================================================
   * DEBUG — PREPARED MATCHER EVIDENCE
   * ============================================================
   */

  console.log(
    "[HobbliVision] PREPARED PARSER:",
    parser
  );

    console.log(
      "[HobbliVision] PREPARED CANDIDATES:",
      items.map((item) => {
      const prepared = prepareCandidate(item);

      return {
        id: prepared.id,
        names: prepared.name,
        sets: prepared.sets,
        years: prepared.years,
        cardNumbers: prepared.cardNumbers,
        source: item.source,
        raw: item,
      };
    })
  );

  const candidates = items
      .map((item) => {
      const preparedItem = prepareCandidate(item);

      return scoreItem(
        preparedItem,
        parser,
        config
      );
    })
    .filter(
      (candidate) =>
        candidate.score >= config.MIN_CANDIDATE_SCORE
    )
    .sort((a, b) => b.score - a.score)
    .slice(0, config.MAX_CANDIDATES);

  const bestMatch = candidates[0] || null;

  return {
    version: MATCHER_VERSION,

    matched: Boolean(bestMatch),

    bestMatch,

    candidates,

    confidence: bestMatch?.score || 0,

    diagnostics: {
      matcherVersion: MATCHER_VERSION,

      inputItemCount: items.length,

      candidateCount: candidates.length,

      parserVersion: parsedOCR.version || null,

      parserClues: {
        player: parsedOCR.player || null,
        set: parsedOCR.set || null,
        year: parsedOCR.year || null,
        cardNumber: parsedOCR.cardNumber || null,
        variations: parsedOCR.variations || [],
      },

      topCandidate: bestMatch
        ? {
            id: bestMatch.id,

            name: bestMatch.itemName,

            score: bestMatch.score,

            source: bestMatch.source || null,

            sourceType: bestMatch.sourceType || null,

            databaseImageCount:
              bestMatch.databaseImages?.length || 0,

            mainImage:
              getMainDatabaseImage(
                bestMatch.item
              )?.image_url || null,
          }
        : null,
    },

    createdAt: new Date().toISOString(),
  };
}


/**
 * ============================================================
 * CONFIG MERGING
 * ============================================================
 */

function mergeConfig(options = {}) {
  return {
    ...CONFIG,

    ...options,

    WEIGHTS: {
      ...CONFIG.WEIGHTS,
      ...(options.WEIGHTS || {}),
    },

    RELATIONSHIPS: {
      ...CONFIG.RELATIONSHIPS,
      ...(options.RELATIONSHIPS || {}),
    },

    CONTRADICTIONS: {
      ...CONFIG.CONTRADICTIONS,
      ...(options.CONTRADICTIONS || {}),
    },
  };
}


/**
 * ============================================================
 * PREPARE PARSER EVIDENCE
 * ============================================================
 *
 * Extracts all useful parser evidence once.
 *
 * This also deduplicates identity/name candidates.
 * ============================================================
 */

function prepareParserEvidence(
    parsedOCR,
    resolvedNameEvidence = null
  ) {
  const nameCandidates = [];

    /**
   * ----------------------------------------------------------
   * RESOLVER-DISCOVERED IDENTITY
   * ----------------------------------------------------------
   *
   * The parser may have produced a noisy OCR identity such as:
   *
   *   "LAHAT TET"
   *
   * while the resolver has discovered:
   *
   *   "LeBron James"
   *
   * The resolver identity is stronger evidence and should be
   * available to the matcher during final scoring.
   */
  if (
    resolvedNameEvidence?.primary?.value &&
    Number(
      resolvedNameEvidence.primary.score
    ) > 0
  ) {
    nameCandidates.push({
      value:
        String(
          resolvedNameEvidence.primary.value
        ).trim(),

      confidence:
        Math.max(
          Number(
            resolvedNameEvidence.primary.score
          ) || 0,

          Number(
            resolvedNameEvidence.confidence
          ) || 0
        ),

      source:
        "resolver",
    });
  }

  if (Array.isArray(parsedOCR.identityCandidates)) {
    parsedOCR.identityCandidates.forEach((candidate) => {
      const value =
        typeof candidate === "string"
          ? candidate
          : candidate?.value;

      if (!value) {
        return;
      }

      nameCandidates.push({
        value: String(value).trim(),
        confidence:
          typeof candidate === "object"
            ? candidate.confidence ?? 0.5
            : 0.5,
        source: "identityCandidate",
      });
    });
  }

  if (Array.isArray(parsedOCR.nameCandidates)) {
    parsedOCR.nameCandidates.forEach((candidate) => {
      const value =
        typeof candidate === "string"
          ? candidate
          : candidate?.value;

      if (!value) {
        return;
      }

      nameCandidates.push({
        value: String(value).trim(),
        confidence:
          typeof candidate === "object"
            ? candidate.confidence ?? 0.5
            : 0.5,
        source: "nameCandidate",
      });
    });
  }

  if (parsedOCR.player) {
    nameCandidates.push({
      value: String(parsedOCR.player).trim(),
      confidence:
        parsedOCR.playerConfidence ?? 0.5,
      source: "player",
    });
  }

  if (parsedOCR.itemName) {
    nameCandidates.push({
      value: String(parsedOCR.itemName).trim(),
      confidence:
        parsedOCR.itemNameConfidence ?? 0.5,
      source: "itemName",
    });
  }

  const normalizeStructuredValue = (
    field,
    defaultConfidence
  ) => {
    if (!field) {
      return null;
    }

    const rawValue =
      typeof field === "object"
        ? field.value ??
          field.text ??
          field.label ??
          null
        : field;

    if (rawValue === null || rawValue === undefined) {
      return null;
    }

    const value = String(rawValue).trim();

    if (!value) {
      return null;
    }

    return {
      value,
      confidence:
        typeof field === "object"
          ? field.confidence ??
            defaultConfidence
          : defaultConfidence,
    };
  };

  return {
    raw: parsedOCR,

    names: dedupeEvidence(nameCandidates),

    set: normalizeStructuredValue(
      parsedOCR.set,
      parsedOCR.setConfidence ?? 0.75
    ),

    year: normalizeStructuredValue(
      parsedOCR.year,
      parsedOCR.yearConfidence ?? 0.8
    ),

    cardNumber: normalizeStructuredValue(
      parsedOCR.cardNumber,
      parsedOCR.cardNumberConfidence ?? 0.75
    ),

    variations: normalizeEvidenceArray(
      parsedOCR.variations,
      0.65
    ),

    keywords: normalizeEvidenceArray(
      parsedOCR.keywords,
      0.65
    ),
  };
}


/**
 * ============================================================
 * PREPARE CANDIDATE
 * ============================================================
 *
 * Candidate normalization happens once per item.
 * ============================================================
 */

function prepareCandidate(item) {
  return {
    raw: item,

    id: getItemId(item),

    name: getItemNameCandidates(item),

    sets: getItemSets(item),

    years: getItemYears(item),

    cardNumbers: getItemCardNumbers(item),

    variations: getItemVariations(item),

    keywords: getItemKeywords(item),

    additionalDetails: getAdditionalDetails(item),

    databaseImages: getDatabaseImages(item),
  };
}


/**
 * ============================================================
 * SCORE ITEM
 * ============================================================
 */

function scoreItem(candidate, parser, config) {
  const nameResult = scoreName(
    candidate,
    parser,
    config
  );

  const setResult = scoreSet(
    candidate,
    parser,
    config
  );

  const yearResult = scoreYear(
    candidate,
    parser
  );

  const cardNumberResult = scoreCardNumber(
    candidate,
    parser
  );

  const variationResult = scoreVariations(
    candidate,
    parser,
    config
  );

  const keywordResult = scoreKeywords(
    candidate,
    parser,
    config
  );

  const additionalDetailsResult =
    scoreAdditionalDetails(
      candidate,
      parser,
      config
    );

  /**
   * ----------------------------------------------------------
   * BASE SCORE
   * ----------------------------------------------------------
   */

  const baseScore =
    nameResult.score * config.WEIGHTS.name +
    setResult.score * config.WEIGHTS.set +
    yearResult.score * config.WEIGHTS.year +
    cardNumberResult.score *
      config.WEIGHTS.cardNumber +
    variationResult.score *
      config.WEIGHTS.variation +
    keywordResult.score *
      config.WEIGHTS.keyword +
    additionalDetailsResult.score *
      config.WEIGHTS.additionalDetails;

  /**
   * ----------------------------------------------------------
   * RELATIONAL EVIDENCE
   * ----------------------------------------------------------
   */

  const relationshipResult =
    scoreRelationships(
      {
        name: nameResult,
        set: setResult,
        year: yearResult,
        cardNumber: cardNumberResult,
        variation: variationResult,
      },
      config
    );

  /**
   * ----------------------------------------------------------
   * CONTRADICTIONS
   * ----------------------------------------------------------
   */

  const contradictionResult =
    scoreContradictions(
      {
        name: nameResult,
        set: setResult,
        year: yearResult,
        cardNumber: cardNumberResult,
        variation: variationResult,
      },
      config
    );

  /**
   * ----------------------------------------------------------
   * IDENTIFIER BONUS
   * ----------------------------------------------------------
   */

  const identifierBonus =
    getIdentifierBonus(
      setResult,
      cardNumberResult,
      config
    );

  /**
   * ----------------------------------------------------------
   * FINAL SCORE
   * ----------------------------------------------------------
   */

  const rawScore =
    baseScore +
    relationshipResult.total +
    identifierBonus -
    contradictionResult.total;

  const score = clamp(
    rawScore,
    0,
    1
  );

  return {
    id: candidate.id,

    item: candidate.raw,

    itemName: getItemName(candidate.raw),

    source:
      candidate.raw.source || null,

    sourceType:
      candidate.raw.sourceType || null,

    databaseImages:
      candidate.databaseImages,

    score,

    confidence: score,

    reasons: buildReasons({
      nameResult,
      setResult,
      yearResult,
      cardNumberResult,
      variationResult,
      keywordResult,
      additionalDetailsResult,
      relationshipResult,
      contradictionResult,
      identifierBonus,
    }),

    scoreBreakdown: {
      baseScore,

      directEvidence: {
        name: nameResult,
        set: setResult,
        year: yearResult,
        cardNumber: cardNumberResult,
        variation: variationResult,
        keyword: keywordResult,
        additionalDetails:
          additionalDetailsResult,
      },

      relationships: relationshipResult,

      contradictions: contradictionResult,

      identifierBonus,
    },
  };
}


/**
 * ============================================================
 * NAME MATCHING
 * ============================================================
 */

function scoreName(candidate, parser, config) {
    const itemNames = candidate.name || [];
    const parserCandidates = parser.names || [];

    /*
     * Resolver evidence comes from fetchMatcherCandidates.js.
     *
     * This is especially important after the second retrieval pass,
     * because the resolver may have discovered the real identity
     * even when the raw OCR name was poor.
     */
    const resolverEvidence =
        candidate.raw?.evidenceResolution?.name;

    const resolverScore =
        Number(resolverEvidence?.score) || 0;

    const resolverValue =
        typeof resolverEvidence?.value === "string"
            ? resolverEvidence.value
            : typeof resolverEvidence?.primary?.value === "string"
                ? resolverEvidence.primary.value
                : null;

    /*
     * If there is no normal parser name evidence, allow resolver
     * evidence to stand on its own.
     */
    if (
        !itemNames.length ||
        !parserCandidates.length
    ) {
        if (resolverScore > 0) {
            return {
                score: clamp(
                    resolverScore,
                    0,
                    1
                ),
                similarity: resolverScore,
                parserValue:
                    resolverValue,
                itemValue:
                    itemNames[0] || null,
                parserConfidence:
                    Number(
                        resolverEvidence?.confidence
                    ) || resolverScore,
                source: "resolver",
            };
        }

        return {
            score: 0,
            similarity: 0,
            parserValue: null,
            itemValue: null,
            parserConfidence: 0,
            source: null,
        };
    }

    let best = {
        score: 0,
        similarity: 0,
        parserValue: null,
        itemValue: null,
        parserConfidence: 0,
        source: null,
    };

    /*
     * First score the normal OCR/parser name evidence.
     */
    for (const parserCandidate of parserCandidates) {
        for (const itemName of itemNames) {
            const similarity =
                stringSimilarity(
                    parserCandidate.value,
                    itemName
                );

            let score = similarity;

            if (
                similarity >=
                config.NAME_FUZZY_THRESHOLD
            ) {
                score =
                    similarity *
                    (
                        Number(
                            parserCandidate.confidence
                        ) || 1
                    );
            }

            if (
              parserCandidate.normalized &&
              parserCandidate.normalized ===
                  String(itemName)
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, " ")
                      .trim()
          ) {
              score = Math.max(
                  score,
                  1
              );
          }

            if (score > best.score) {
                best = {
                    score: clamp(
                        score,
                        0,
                        1
                    ),
                    similarity,
                    parserValue:
                        parserCandidate.value,
                    itemValue:
                        itemName,
                    parserConfidence:
                        Number(
                            parserCandidate.confidence
                        ) || 0,
                    source: "parser",
                };
            }
        }
    }

    /*
     * Now compare the resolver's identity evidence.
     *
     * This is the critical addition.
     *
     * If the resolver discovered "LeBron James" with a strong
     * score, that evidence can beat the noisy OCR result such as
     * "LAHAT TET".
     */
    if (
        resolverScore >
        best.score
    ) {
        best = {
            score: clamp(
                resolverScore,
                0,
                1
            ),
            similarity: resolverScore,
            parserValue:
                resolverValue,
            itemValue:
                itemNames[0] || null,
            parserConfidence:
                Number(
                    resolverEvidence?.confidence
                ) || resolverScore,
            source: "resolver",
        };
    }

    return best;
}


/**
 * ============================================================
 * SET MATCHING
 * ============================================================
 */

function scoreSet(candidate, parser, config) {
  if (!parser.set) {
    return emptyScore("No set evidence");
  }

  if (!candidate.sets.length) {
    return emptyScore(
      "Candidate has no set information"
    );
  }

  let best = {
    score: 0,
    similarity: 0,
    parserValue: parser.set.value,
    itemValue: null,
    confidence: parser.set.confidence,
  };

  candidate.sets.forEach((itemSet) => {
    const similarity = stringSimilarity(
      parser.set.value,
      itemSet
    );

    const exact =
      normalize(parser.set.value) ===
      normalize(itemSet);

    let score = 0;

    if (exact) {
      score = parser.set.confidence;
    } else if (
      similarity >= config.SET_FUZZY_THRESHOLD
    ) {
      score =
        similarity *
        parser.set.confidence;
    }

    if (score > best.score) {
      best = {
        score: clamp(score, 0, 1),

        similarity,

        parserValue:
          parser.set.value,

        itemValue: itemSet,

        confidence:
          parser.set.confidence,
      };
    }
  });

  return best;
}


/**
 * ============================================================
 * YEAR MATCHING
 * ============================================================
 *
 * Years are treated as categorical identifiers.
 *
 * We deliberately do NOT fuzzy-match years.
 *
 * 1989 vs 1988 is not "mostly correct".
 * ============================================================
 */

function scoreYear(candidate, parser) {
  if (!parser.year) {
    return emptyScore("No year evidence");
  }

  if (!candidate.years.length) {
    return emptyScore(
      "Candidate has no year information"
    );
  }

  const parserYear =
    normalizeYear(parser.year);

  if (!parserYear) {
    return emptyScore(
      "Parser year is invalid"
    );
  }

  const exact = candidate.years.some(
    (year) =>
      normalizeYear(year) === parserYear
  );

  return {
    score: exact
      ? parser.year.confidence
      : 0,

    parserValue: parser.year.value,

    itemValue: exact
      ? parserYear
      : candidate.years[0],

    confidence: parser.year.confidence,

    exact,
  };
}


/**
 * ============================================================
 * CARD NUMBER MATCHING
 * ============================================================
 */

function scoreCardNumber(candidate, parser) {
  if (!parser.cardNumber) {
    return emptyScore(
      "No card/set identifier evidence"
    );
  }

  if (!candidate.cardNumbers.length) {
    return emptyScore(
      "Candidate has no card/set identifier"
    );
  }

  const parserNumber =
    normalizeCardNumber(
      parser.cardNumber.value
    );

  if (!parserNumber) {
    return emptyScore(
      "Parser card/set identifier is invalid"
    );
  }

  let best = {
    score: 0,
    similarity: 0,
    parserValue:
      parser.cardNumber.value,
    itemValue: null,
    confidence:
      parser.cardNumber.confidence,
    exact: false,
  };

  candidate.cardNumbers.forEach((number) => {
    const candidateNumber =
      normalizeCardNumber(number);

    if (!candidateNumber) {
      return;
    }

    const exact =
      candidateNumber === parserNumber;

    if (exact) {
      best = {
        score:
          parser.cardNumber.confidence,

        similarity: 1,

        parserValue:
          parser.cardNumber.value,

        itemValue: number,

        confidence:
          parser.cardNumber.confidence,

        exact: true,
      };

      return;
    }

    /**
     * We allow limited fuzzy similarity for numeric card
     * numbers, but only when the strings are reasonably
     * close and contain a meaningful identifier.
     */
    const similarity =
      stringSimilarity(
        parserNumber,
        candidateNumber
      );

    if (
      similarity >= 0.75 &&
      similarity > best.similarity
    ) {
      best = {
        score:
          similarity *
          parser.cardNumber.confidence,

        similarity,

        parserValue:
          parser.cardNumber.value,

        itemValue: number,

        confidence:
          parser.cardNumber.confidence,

        exact: false,
      };
    }
  });

  return best.score
    ? best
    : emptyScore(
        "No sufficiently strong identifier match"
      );
}


/**
 * ============================================================
 * VARIATION MATCHING
 * ============================================================
 */

function scoreVariations(
  candidate,
  parser,
  config
) {
  if (!parser.variations.length) {
    return emptyScore(
      "No variation evidence"
    );
  }

  if (!candidate.variations.length) {
    return emptyScore(
      "Candidate has no variation information"
    );
  }

  const matched = [];

  parser.variations.forEach(
    (parserVariation) => {
      let best = null;

      candidate.variations.forEach(
        (itemVariation) => {
          const similarity =
            stringSimilarity(
              parserVariation.value,
              itemVariation
            );

          if (
            !best ||
            similarity > best.similarity
          ) {
            best = {
              parserValue:
                parserVariation.value,

              itemValue:
                itemVariation,

              similarity,
            };
          }
        }
      );

      if (
        best &&
        best.similarity >=
          config.VARIATION_FUZZY_THRESHOLD
      ) {
        matched.push({
          ...best,

          confidence:
            parserVariation.confidence,
        });
      }
    }
  );

  if (!matched.length) {
    return {
      score: 0,
      matched: [],
      reason:
        "No sufficiently strong variation match",
    };
  }

  const score =
    matched.reduce(
      (total, match) =>
        total +
        match.similarity *
          match.confidence,
      0
    ) /
    parser.variations.length;

  return {
    score: clamp(score, 0, 1),

    matched,

    confidence:
      matched.reduce(
        (total, match) =>
          total + match.confidence,
        0
      ) / matched.length,
  };
}


/**
 * ============================================================
 * KEYWORD MATCHING
 * ============================================================
 */

function scoreKeywords(
  candidate,
  parser,
  config
) {
  if (!parser.keywords.length) {
    return emptyScore(
      "No keyword evidence"
    );
  }

  if (!candidate.keywords.length) {
    return emptyScore(
      "Candidate has no keyword information"
    );
  }

  let matched = 0;

  const matches = [];

  parser.keywords.forEach(
    (parserKeyword) => {
      let best = null;

      candidate.keywords.forEach(
        (itemKeyword) => {
          const similarity =
            stringSimilarity(
              parserKeyword.value,
              itemKeyword
            );

          if (
            !best ||
            similarity > best.similarity
          ) {
            best = {
              parserValue:
                parserKeyword.value,

              itemValue:
                itemKeyword,

              similarity,
            };
          }
        }
      );

      if (
        best &&
        best.similarity >=
          config.KEYWORD_FUZZY_THRESHOLD
      ) {
        matched++;

        matches.push(best);
      }
    }
  );

  return {
    score:
      matched /
      parser.keywords.length,

    matched,

    total:
      parser.keywords.length,

    matches,
  };
}


/**
 * ============================================================
 * ADDITIONAL DETAILS MATCHING
 * ============================================================
 */

function scoreAdditionalDetails(
  candidate,
  parser,
  config
) {
  if (!candidate.additionalDetails.length) {
    return emptyScore(
      "Candidate has no additional details"
    );
  }

  const parserText =
    getParserSearchText(parser.raw);

  if (!parserText) {
    return emptyScore(
      "No parser text for additional details"
    );
  }

  let best = {
    score: 0,
    parserValue: parserText,
    itemValue: null,
  };

  candidate.additionalDetails.forEach(
    (detail) => {
      const normalizedDetail =
        normalize(detail);

      if (!normalizedDetail) {
        return;
      }

      if (
        parserText.includes(
          normalizedDetail
        )
      ) {
        if (best.score < 1) {
          best = {
            score: 1,

            parserValue: parserText,

            itemValue: detail,
          };
        }

        return;
      }

      const similarity =
        stringSimilarity(
          normalizedDetail,
          parserText
        );

      if (
        similarity > best.score
      ) {
        best = {
          score: similarity,

          parserValue: parserText,

          itemValue: detail,
        };
      }
    }
  );

  return best;
}


/**
 * ============================================================
 * RELATIONAL EVIDENCE
 * ============================================================
 *
 * This is the major V2 improvement.
 *
 * Direct evidence answers:
 *
 *   name matches
 *   set matches
 *   year matches
 *
 * Relational evidence answers:
 *
 *   name AND set agree
 *   name AND year agree
 *   set AND year agree
 *   set AND card number agree
 *   name AND set AND year agree
 *   name AND set AND year AND card number agree
 *
 * Relationships only activate when both pieces of evidence
 * are sufficiently strong.
 * ============================================================
 */

function scoreRelationships(
  results,
  config
) {
  const relationships = [];

  let total = 0;

  const nameStrong =
    isStrongEvidence(
      results.name,
      config
    );

  const setStrong =
    isStrongEvidence(
      results.set,
      config
    );

  const yearStrong =
    isStrongEvidence(
      results.year,
      config
    );

  const cardStrong =
    isStrongEvidence(
      results.cardNumber,
      config
    );

  const variationStrong =
    isStrongEvidence(
      results.variation,
      config
    );

  /**
   * Name + Set
   */
  if (nameStrong && setStrong) {
    total +=
      config.RELATIONSHIPS.nameSet;

    relationships.push({
      type: "name_set",
      bonus:
        config.RELATIONSHIPS.nameSet,
      evidence: [
        "name",
        "set",
      ],
    });
  }

  /**
   * Name + Year
   */
  if (nameStrong && yearStrong) {
    total +=
      config.RELATIONSHIPS.nameYear;

    relationships.push({
      type: "name_year",
      bonus:
        config.RELATIONSHIPS.nameYear,
      evidence: [
        "name",
        "year",
      ],
    });
  }

  /**
   * Set + Year
   */
  if (setStrong && yearStrong) {
    total +=
      config.RELATIONSHIPS.setYear;

    relationships.push({
      type: "set_year",
      bonus:
        config.RELATIONSHIPS.setYear,
      evidence: [
        "set",
        "year",
      ],
    });
  }

  /**
   * Set + Card Number
   */
  if (setStrong && cardStrong) {
    total +=
      config.RELATIONSHIPS.setCardNumber;

    relationships.push({
      type: "set_card_number",
      bonus:
        config.RELATIONSHIPS.setCardNumber,
      evidence: [
        "set",
        "cardNumber",
      ],
    });
  }

  /**
   * Name + Set + Year
   */
  if (
    nameStrong &&
    setStrong &&
    yearStrong
  ) {
    total +=
      config.RELATIONSHIPS.nameSetYear;

    relationships.push({
      type: "name_set_year",
      bonus:
        config.RELATIONSHIPS.nameSetYear,
      evidence: [
        "name",
        "set",
        "year",
      ],
    });
  }

  /**
   * Name + Set + Year + Card Number
   */
  if (
    nameStrong &&
    setStrong &&
    yearStrong &&
    cardStrong
  ) {
    total +=
      config.RELATIONSHIPS
        .nameSetYearCardNumber;

    relationships.push({
      type:
        "name_set_year_card_number",

      bonus:
        config.RELATIONSHIPS
          .nameSetYearCardNumber,

      evidence: [
        "name",
        "set",
        "year",
        "cardNumber",
      ],
    });
  }

  if (
    nameStrong &&
    yearStrong &&
    cardStrong &&
    !setStrong
  ) {
    total += 0.10;

    relationships.push({
      type: "name_year_card_number",
      bonus: 0.10,
      evidence: [
        "name",
        "year",
        "cardNumber",
      ],
    });
  }

  /**
   * Variation + Set
   */
  if (
    variationStrong &&
    setStrong
  ) {
    total +=
      config.RELATIONSHIPS
        .variationSet;

    relationships.push({
      type: "variation_set",

      bonus:
        config.RELATIONSHIPS
          .variationSet,

      evidence: [
        "variation",
        "set",
      ],
    });
  }

  return {
    total: clamp(total, 0, 1),

    relationships,
  };
}


/**
 * ============================================================
 * CONTRADICTION DETECTION
 * ============================================================
 *
 * V2 distinguishes:
 *
 *   UNKNOWN
 *       candidate has no information
 *
 * from:
 *
 *   CONTRADICTION
 *       candidate has information that disagrees
 *
 * Unknown does not hurt a candidate.
 * Contradiction does.
 * ============================================================
 */

function scoreContradictions(
  results,
  config
) {
  const contradictions = [];

  let total = 0;

  /**
   * Set contradiction.
   */
  if (
    results.set?.score === 0 &&
    results.set?.parserValue &&
    results.set?.itemValue
  ) {
    const penalty =
      config.CONTRADICTIONS.set;

    total += penalty;

    contradictions.push({
      type: "set_mismatch",
      penalty,
      parserValue:
        results.set.parserValue,
      itemValue:
        results.set.itemValue,
    });
  }

  /**
   * Year contradiction.
   *
   * Only treat this as a contradiction when both sides have
   * actual year information.
   */
  if (
    results.year?.parserValue &&
    results.year?.itemValue &&
    !results.year.exact
  ) {
    const penalty =
      config.CONTRADICTIONS.year;

    total += penalty;

    contradictions.push({
      type: "year_mismatch",
      penalty,
      parserValue:
        results.year.parserValue,
      itemValue:
        results.year.itemValue,
    });
  }

  /**
   * Card number contradiction.
   */
  if (
    results.cardNumber?.parserValue &&
    results.cardNumber?.itemValue &&
    results.cardNumber.score === 0
  ) {
    const penalty =
      config.CONTRADICTIONS.cardNumber;

    total += penalty;

    contradictions.push({
      type: "card_number_mismatch",
      penalty,
      parserValue:
        results.cardNumber.parserValue,
      itemValue:
        results.cardNumber.itemValue,
    });
  }

  /**
   * Variation contradiction.
   */
  if (
    results.variation?.matched?.length === 0 &&
    results.variation?.reason
  ) {
    /**
     * We intentionally keep this conservative.
     *
     * Variation OCR is often noisy. A missing variation match
     * alone should not destroy an otherwise strong candidate.
     */
  }

  return {
    total: clamp(total, 0, 1),

    contradictions,
  };
}


/**
 * ============================================================
 * IDENTIFIER BONUS
 * ============================================================
 */

function getIdentifierBonus(
  setResult,
  cardNumberResult,
  config
) {
  if (
    setResult.score >=
      config.STRONG_MATCH_THRESHOLD &&
    cardNumberResult.score >=
      config.STRONG_MATCH_THRESHOLD
  ) {
    return config.EXACT_IDENTIFIER_BONUS;
  }

  return 0;
}


/**
 * ============================================================
 * REASONS
 * ============================================================
 */

function buildReasons({
  nameResult,
  setResult,
  yearResult,
  cardNumberResult,
  variationResult,
  keywordResult,
  additionalDetailsResult,
  relationshipResult,
  contradictionResult,
  identifierBonus,
}) {
  const reasons = [];

  if (nameResult.score >= 0.90) {
    reasons.push(
      `Strong name match: ${nameResult.parserValue}`
    );
  } else if (nameResult.score >= 0.70) {
    reasons.push(
      `Good name match: ${nameResult.parserValue}`
    );
  }

  if (setResult.score >= 0.90) {
    reasons.push(
      `Set match: ${setResult.itemValue}`
    );
  } else if (setResult.score >= 0.70) {
    reasons.push(
      `Probable set match: ${setResult.itemValue}`
    );
  }

  if (yearResult.score >= 0.90) {
    reasons.push(
      `Year match: ${yearResult.itemValue}`
    );
  }

  if (cardNumberResult.score >= 0.90) {
    reasons.push(
      `Card/set identifier match: ${cardNumberResult.itemValue}`
    );
  }

  if (variationResult.score >= 0.70) {
    reasons.push(
      "Variation evidence matches"
    );
  }

  if (keywordResult.score > 0) {
    reasons.push(
      "Additional keyword evidence matches"
    );
  }

  if (
    additionalDetailsResult?.score >=
    0.70
  ) {
    reasons.push(
      `Additional details match: ${additionalDetailsResult.itemValue}`
    );
  }

  relationshipResult.relationships.forEach(
    (relationship) => {
      switch (relationship.type) {
        case "name_set":
          reasons.push(
            "Name and set evidence reinforce each other"
          );
          break;

        case "name_year":
          reasons.push(
            "Name and year evidence reinforce each other"
          );
          break;

        case "set_year":
          reasons.push(
            "Set and year evidence reinforce each other"
          );
          break;

        case "set_card_number":
          reasons.push(
            "Set and card identifier reinforce each other"
          );
          break;

        case "name_set_year":
          reasons.push(
            "Name, set, and year form a coherent identity"
          );
          break;

        case "name_set_year_card_number":
          reasons.push(
            "Name, set, year, and identifier strongly agree"
          );
          break;

        case "variation_set":
          reasons.push(
            "Variation and set evidence reinforce each other"
          );
          break;

        default:
          break;
      }
    }
  );

  contradictionResult.contradictions.forEach(
    (contradiction) => {
      reasons.push(
        `Contradiction: ${contradiction.type.replace(
          /_/g,
          " "
        )}`
      );
    }
  );

  if (identifierBonus > 0) {
    reasons.push(
      "Strong set/identifier combination"
    );
  }

  if (!reasons.length) {
    reasons.push(
      "Partial evidence match"
    );
  }

  return reasons;
}


/**
 * ============================================================
 * DATABASE ITEM NORMALIZATION
 * ============================================================
 */

function getItemId(item) {
  return (
    item.id ??
    item.itemId ??
    item.cardId ??
    item._id ??
    null
  );
}


function getItemName(item) {
  return (
    item.itemName ||
    item.name ||
    item.player ||
    item.title ||
    item.user_specific_name ||
    item.userSpecificName ||
    item.item?.name ||
    item.originalRow?.user_specific_name ||
    ""
  );
}


function getItemNameCandidates(item) {
  const values = [
    item.itemName,
    item.name,
    item.player,
    item.title,
    item.subject,

    item.user_specific_name,

    item.item?.name,

    item.originalRow?.user_specific_name,
    item.originalRow?.name,

    item.userSpecificName,

    item.additional_details?.Team,
    item.additional_details?.Identifier,

    item.item?.additional_details?.Team,
    item.item?.additional_details?.Identifier,

    item.originalRow?.additional_details?.Team,
    item.originalRow?.additional_details?.Identifier,
  ];

  if (Array.isArray(item.nameAliases)) {
    values.push(
      ...item.nameAliases
    );
  }

  if (Array.isArray(item.aliases)) {
    values.push(
      ...item.aliases
    );
  }

  return uniqueStrings(values);
}


function getItemSets(item) {
  return uniqueStrings([
    item.set,
    item.setName,
    item.set_name,
    item.series,
    item.collection,

    item.user_specific_set_name,

    item.item?.set_name,

    item.originalRow?.user_specific_set_name,
    item.originalRow?.set_name,
  ]);
}


function getItemYears(item) {
  return uniqueStrings([
    item.year,
    item.releaseYear,
    item.cardYear,

    item.user_specific_year,

    item.item?.year,

    item.originalRow?.user_specific_year,
    item.originalRow?.year,
  ]);
}


function getItemCardNumbers(item) {
  return uniqueStrings([
    item.cardNumber,
    item.card_number,
    item.number,
    item.cardNo,

    item.user_specific_card_number,

    item.item?.card_number,

    item.attributes?.card_number,

    item.originalRow?.attributes?.card_number,
  ]);
}


function getItemVariations(item) {
  const values = [
    item.variation,
    item.variant,
    item.parallel,
    item.insert,

    ...(Array.isArray(item.variants)
      ? item.variants
      : []),

    item.attributes?.parallel,
    item.attributes?.variation,
    item.attributes?.variant,

    item.originalRow?.attributes?.parallel,
    item.originalRow?.attributes?.variation,
    item.originalRow?.attributes?.variant,
  ];

  if (Array.isArray(item.variations)) {
    values.push(
      ...item.variations
    );
  }

  return uniqueStrings(values);
}


function getItemKeywords(item) {
  const values = [
    item.set,
    item.setName,
    item.set_name,

    item.player,
    item.itemName,
    item.name,

    item.brand,
    item.manufacturer,

    item.variation,
    item.variant,
    item.parallel,

    item.category,
    item.subcategory,

    item.item?.name,
    item.item?.set_name,
    item.item?.brand,

    item.attributes?.parallel,
    item.attributes?.variation,
    item.attributes?.variant,
  ];

  if (Array.isArray(item.keywords)) {
    values.push(
      ...item.keywords
    );
  }

  if (Array.isArray(item.variants)) {
    values.push(
      ...item.variants
    );
  }

  return uniqueStrings(values);
}


/**
 * ============================================================
 * ADDITIONAL DETAILS
 * ============================================================
 */

function getAdditionalDetails(item) {
  const details = [];

  const sources = [
    item.additional_details,
    item.item?.additional_details,
    item.originalRow?.additional_details,
  ];

  sources.forEach((source) => {
    if (!source) {
      return;
    }

    if (typeof source === "string") {
      details.push(source);
      return;
    }

    if (typeof source === "object") {
      Object.entries(source).forEach(
        ([key, value]) => {
          if (
            value === null ||
            value === undefined
          ) {
            return;
          }

          details.push(
            `${key}: ${value}`
          );
        }
      );
    }
  });

  return uniqueStrings(details);
}


/**
 * ============================================================
 * PARSER SEARCH TEXT
 * ============================================================
 */

function getParserSearchText(parsedOCR) {
  const values = [
    parsedOCR.player,
    parsedOCR.itemName,
    parsedOCR.set,
    parsedOCR.year,
    parsedOCR.cardNumber,

    ...(Array.isArray(parsedOCR.keywords)
      ? parsedOCR.keywords.map(
          (keyword) =>
            typeof keyword === "string"
              ? keyword
              : keyword?.value
        )
      : []),

    ...(Array.isArray(parsedOCR.variations)
      ? parsedOCR.variations.map(
          (variation) =>
            typeof variation === "string"
              ? variation
              : variation?.value
        )
      : []),

    ...(Array.isArray(
      parsedOCR.identityCandidates
    )
      ? parsedOCR.identityCandidates.map(
          (candidate) =>
            candidate?.value
        )
      : []),

    ...(Array.isArray(
      parsedOCR.nameCandidates
    )
      ? parsedOCR.nameCandidates.map(
          (candidate) =>
            typeof candidate === "string"
              ? candidate
              : candidate?.value
        )
      : []),
  ];

  return normalize(
    uniqueStrings(values).join(" ")
  );
}


/**
 * ============================================================
 * DATABASE IMAGE NORMALIZATION
 * ============================================================
 */

function getDatabaseImages(item) {
  const sources = [
    item.databaseImages,
    item.item_images,
    item.itemImages,

    item.originalRow?.databaseImages,
    item.originalRow?.item_images,
    item.originalRow?.itemImages,

    item.item?.databaseImages,
    item.item?.item_images,
    item.item?.itemImages,
  ];

  const images = [];

  sources.forEach((source) => {
    if (!Array.isArray(source)) {
      return;
    }

    source.forEach((image) => {
      if (!image) {
        return;
      }

      const imageUrl =
        image.image_url ||
        image.imageUrl ||
        image.url;

      if (!imageUrl) {
        return;
      }

      images.push({
        id: image.id ?? null,

        user_item_id:
          image.user_item_id ??
          image.userItemId ??
          null,

        image_url: imageUrl,

        position:
          image.position !== undefined &&
          image.position !== null
            ? Number(image.position)
            : null,

        created_at:
          image.created_at ??
          image.createdAt ??
          null,
      });
    });
  });

  const seen = new Set();

  return images.filter(
    (image) => {
      const key = image.id
        ? `id:${image.id}`
        : `url:${image.image_url}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);

      return true;
    }
  );
}


/**
 * ============================================================
 * MAIN DATABASE IMAGE
 * ============================================================
 */

function getMainDatabaseImage(item) {
  const images =
    Array.isArray(item.databaseImages)
      ? item.databaseImages
      : getDatabaseImages(item);

  if (!images.length) {
    return null;
  }

  return [...images].sort(
    (a, b) => {
      const positionA =
        a.position === null
          ? Number.MAX_SAFE_INTEGER
          : a.position;

      const positionB =
        b.position === null
          ? Number.MAX_SAFE_INTEGER
          : b.position;

      return positionA - positionB;
    }
  )[0];
}


/**
 * ============================================================
 * EVIDENCE HELPERS
 * ============================================================
 */

function normalizeEvidenceArray(
  values,
  defaultConfidence
) {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => {
      if (!value) {
        return null;
      }

      if (typeof value === "string") {
        return {
          value: value.trim(),
          confidence: defaultConfidence,
        };
      }

      if (
        typeof value === "object" &&
        value.value
      ) {
        return {
          value: String(
            value.value
          ).trim(),

          confidence:
            value.confidence ??
            defaultConfidence,
        };
      }

      return null;
    })
    .filter(
      (value) =>
        value?.value
    );
}


function dedupeEvidence(values) {
  const seen = new Map();

  values.forEach((entry) => {
    const key = normalize(
      entry.value
    );

    if (!key) {
      return;
    }

    const existing =
      seen.get(key);

    if (
      !existing ||
      entry.confidence >
        existing.confidence
    ) {
      seen.set(
        key,
        entry
      );
    }
  });

  return [...seen.values()];
}


function isStrongEvidence(
  result,
  config
) {
  if (!result) {
    return false;
  }

  return (
    result.score >=
      config.STRONG_MATCH_THRESHOLD &&
    (result.confidence === undefined ||
      result.confidence >=
        config.RELATIONSHIP_CONFIDENCE_THRESHOLD)
  );
}


/**
 * ============================================================
 * STRING SIMILARITY
 * ============================================================
 */

function stringSimilarity(a, b) {
  const left = normalize(a);
  const right = normalize(b);

  if (!left || !right) {
    return 0;
  }

  if (left === right) {
    return 1;
  }

  const distance =
    levenshteinDistance(
      left,
      right
    );

  return clamp(
    1 -
      distance /
        Math.max(
          left.length,
          right.length
        ),
    0,
    1
  );
}


/**
 * ============================================================
 * NORMALIZATION
 * ============================================================
 */

function normalize(value) {
  const rawValue =
    value && typeof value === "object"
      ? value.value ??
        value.text ??
        value.label ??
        value.name ??
        value.raw ??
        ""
      : value;

  return String(rawValue || "")
    .toUpperCase()
    .replace(
      /[^A-Z0-9]+/g,
      " "
    )
    .replace(
      /\s+/g,
      " "
    )
    .trim();
}


function normalizeYear(value) {
  const normalized =
    normalize(value);

  const match =
    normalized.match(
      /\b(18|19|20)\d{2}\b/
    );

  return match
    ? match[0]
    : null;
}


function normalizeCardNumber(value) {
  return normalize(value)
    .replace(
      /^NUMBER\s+/,
      ""
    )
    .replace(
      /^CARD\s+/,
      ""
    )
    .replace(
      /^NO\s+/,
      ""
    )
    .trim();
}


/**
 * ============================================================
 * LEVENSHTEIN
 * ============================================================
 */

function levenshteinDistance(a, b) {
  /**
   * Small optimization:
   *
   * Keep the shorter string on the horizontal axis.
   */
  if (a.length > b.length) {
    [a, b] = [b, a];
  }

  let previous =
    Array.from(
      { length: a.length + 1 },
      (_, index) => index
    );

  for (
    let j = 1;
    j <= b.length;
    j++
  ) {
    const current =
      new Array(
        a.length + 1
      );

    current[0] = j;

    for (
      let i = 1;
      i <= a.length;
      i++
    ) {
      const cost =
        a[i - 1] ===
        b[j - 1]
          ? 0
          : 1;

      current[i] =
        Math.min(
          current[i - 1] + 1,
          previous[i] + 1,
          previous[i - 1] +
            cost
        );
    }

    previous = current;
  }

  return previous[
    a.length
  ];
}


/**
 * ============================================================
 * UNIQUE STRINGS
 * ============================================================
 */

function uniqueStrings(values) {
  return [
    ...new Set(
      values
        .flatMap(
          (value) =>
            Array.isArray(value)
              ? value
              : [value]
        )
        .filter(
          (value) =>
            value !== null &&
            value !== undefined &&
            String(value).trim()
        )
        .map(
          (value) =>
            String(value).trim()
        )
    ),
  ];
}


/**
 * ============================================================
 * EMPTY SCORE
 * ============================================================
 */

function emptyScore(reason = "") {
  return {
    score: 0,
    reason,
  };
}


/**
 * ============================================================
 * UTILITIES
 * ============================================================
 */

function clamp(
  value,
  min,
  max
) {
  return Math.max(
    min,
    Math.min(
      max,
      value
    )
  );
}


/**
 * ============================================================
 * EMPTY RESULT
 * ============================================================
 */

function createEmptyMatchResult(
  reason
) {
  return {
    version:
      MATCHER_VERSION,

    matched: false,

    bestMatch: null,

    candidates: [],

    confidence: 0,

    diagnostics: {
      matcherVersion:
        MATCHER_VERSION,

      inputItemCount: 0,

      candidateCount: 0,

      reason,
    },

    createdAt:
      new Date().toISOString(),
  };
}

export default matchItems;

